import { USER_ROLES } from '../constants';

export const ROLE_HIERARCHY: Record<string, number> = {
  [USER_ROLES.ADMIN]: 100,
  [USER_ROLES.MANAGER]: 50,
  [USER_ROLES.USER]: 10,
} as const;

export const DEFAULT_ROLE_PRIORITY = 0;

export const ROLE_NAMES_BY_PRIORITY = Object.keys(ROLE_HIERARCHY).sort(
  (a, b) => ROLE_HIERARCHY[b] - ROLE_HIERARCHY[a]
);

export type RoleName = (typeof USER_ROLES)[keyof typeof USER_ROLES];

export interface RoleObject {
  id?: number;
  roleId?: string | number;
  roleName: string;
}

export function getRolePriority(roleName: string): number {
  return ROLE_HIERARCHY[roleName] ?? DEFAULT_ROLE_PRIORITY;
}

export function getHighestRole(roles: (RoleObject | string)[] | null | undefined): string | null {
  if (!roles || roles.length === 0) {
    return null;
  }

  let highestRole: string | null = null;
  let highestPriority = -1;

  for (const role of roles) {
    const roleName = typeof role === 'string' ? role : role.roleName;
    const priority = getRolePriority(roleName);
    if (priority > highestPriority) {
      highestPriority = priority;
      highestRole = roleName;
    }
  }

  return highestRole;
}

export function hasAtLeastRole(
  userRoles: (RoleObject | string)[] | null | undefined,
  minimumRole: string
): boolean {
  const highestRole = getHighestRole(userRoles);
  if (!highestRole) {
    return false;
  }

  const userPriority = getRolePriority(highestRole);
  const requiredPriority = getRolePriority(minimumRole);

  return userPriority >= requiredPriority;
}

export function hasExactHighestRole(
  userRoles: (RoleObject | string)[] | null | undefined,
  roleName: string
): boolean {
  const highestRole = getHighestRole(userRoles);
  return highestRole?.toLowerCase() === roleName.toLowerCase();
}

export function hasRole(
  userRoles: (RoleObject | string)[] | null | undefined,
  roleName: string
): boolean {
  if (!userRoles || userRoles.length === 0) {
    return false;
  }

  return userRoles.some((role) => {
    const name = typeof role === 'string' ? role : role.roleName;
    return name.toLowerCase() === roleName.toLowerCase();
  });
}

export function isOnlyRole(
  userRoles: (RoleObject | string)[] | null | undefined,
  roleName: string
): boolean {
  if (!userRoles || userRoles.length !== 1) {
    return false;
  }

  const role = userRoles[0];
  const name = typeof role === 'string' ? role : role.roleName;
  return name.toLowerCase() === roleName.toLowerCase();
}

export function getAccessibleRoles(roleName: string): string[] {
  const priority = getRolePriority(roleName);
  return ROLE_NAMES_BY_PRIORITY.filter((r) => getRolePriority(r) <= priority);
}

export function compareRoles(roleA: string, roleB: string): number {
  return getRolePriority(roleA) - getRolePriority(roleB);
}

export function sortRolesByPriority(roles: (RoleObject | string)[]): (RoleObject | string)[] {
  return [...roles].sort((a, b) => {
    const nameA = typeof a === 'string' ? a : a.roleName;
    const nameB = typeof b === 'string' ? b : b.roleName;
    return getRolePriority(nameB) - getRolePriority(nameA);
  });
}

export function canAccessAdmin(userRoles: (RoleObject | string)[] | null | undefined): boolean {
  return hasAtLeastRole(userRoles, USER_ROLES.ADMIN);
}

export function canAccessManager(userRoles: (RoleObject | string)[] | null | undefined): boolean {
  return hasAtLeastRole(userRoles, USER_ROLES.MANAGER);
}

export function getRoleLabel(roleName: string): string {
  const labels: Record<string, string> = {
    [USER_ROLES.ADMIN]: 'Amministratore',
    [USER_ROLES.MANAGER]: 'Manager',
    [USER_ROLES.USER]: 'Utente',
  };
  return labels[roleName] || roleName;
}

export function getRoleBadgeColor(roleName: string): string {
  const colors: Record<string, string> = {
    [USER_ROLES.ADMIN]: '#AD1122',
    [USER_ROLES.MANAGER]: '#BC952E',
    [USER_ROLES.USER]: '#666666',
  };
  return colors[roleName] || '#999999';
}

export function isCustomer(userRoles: (RoleObject | string)[] | null | undefined): boolean {
  const highestRole = getHighestRole(userRoles);
  return highestRole === USER_ROLES.USER;
}

export function isStaffMember(userRoles: (RoleObject | string)[] | null | undefined): boolean {
  return hasAtLeastRole(userRoles, USER_ROLES.MANAGER);
}
